import * as React from "react";
import { BaseType } from "../../../../application/domain/base/base-type";
import { useState } from "react";
import { MarkdownPageEditor } from "../markdown-editor/markdown";
import { connectService } from "@graffer-inc/smart-apply";
import {
  ProcedurePresentationContextProps,
  ProcedurePresentationService,
} from "../../../../application/service/smart-apply/procedure-detail/procedure-presentation-service";
import { useMount } from "../../../../utils/custom-hooks";
import { ProcedureEntity } from "../../../../application/domain/procedures/procedure-entity";
import { UpdateButton } from "./update-button";
import { RBACHidden } from "../../../helpers/rbac/rbac";
import { useRBAC } from "../../../helpers/rbac/use-rbac";
import { useAuthContextV2 } from "../../../../auth-state";

export const PresentationSettings = ["DOOR_PAGE", "COMPLETE_PAGE"] as const;

export type PresentationSettingLiterals = (typeof PresentationSettings)[number];

class PresentationSettingUIState extends BaseType<PresentationSettingLiterals> {
  static doorPage() {
    return new PresentationSettingUIState("DOOR_PAGE");
  }

  static completePage() {
    return new PresentationSettingUIState("COMPLETE_PAGE");
  }
}

export const PresentationSetting: React.FC<
  ProcedurePresentationContextProps
> = ({
  procedure,
  onMountSetting,
  onPresentationUpdate,
  onSave,
  presentation,
}) => {
  const hasPermission = useRBAC();
  const { auth } = useAuthContextV2();
  const [uiState, setState] = useState(PresentationSettingUIState.doorPage());

  useMount(() => {
    if (procedure) {
      onMountSetting(procedure);
    }
  });

  const canEditPresentation = hasPermission({
    target: procedure,
    requirePermissions: "procedures.presentation_update",
  });

  if (!procedure) {
    // 手続き詳細画面を開けている時点でprocedureがundefinedとなることはまずないと考えられるので、
    // この条件を満たす際のハンドリングは重要視しない
    return <></>;
  }

  return (
    <div>
      <div className="columns">
        <div className="column has-text-right buttons">
          <div className="select mr-2">
            <select
              onChange={(e) => {
                setState(
                  new PresentationSettingUIState(
                    e.target.value as PresentationSettingLiterals,
                  ),
                );
              }}
            >
              <option value="DOOR_PAGE">トップページ</option>
              <option value="COMPLETE_PAGE">完了ページ</option>
            </select>
          </div>
          {procedure && (
            <RBACHidden
              target={procedure}
              requirePermissions="procedures.presentation_update"
            >
              <UpdateButton
                procedure={procedure}
                canModifyAfterPublish={true}
                onClick={onSave}
              />
            </RBACHidden>
          )}
        </div>
      </div>
      {presentation &&
        uiState.matchType({
          DOOR_PAGE: (
            <MarkdownPageEditor
              key={"door_page"}
              initialMarkdown={presentation.doorPageMarkdown || ""}
              onChange={(e) => {
                onPresentationUpdate({
                  ...presentation,
                  doorPageMarkdown: e,
                });
              }}
              isShowEditor={canEditPresentation}
              isServiceProvider={auth.isServiceProviderAccount()}
            />
          ),
          COMPLETE_PAGE: (
            <MarkdownPageEditor
              key={"complete_page"}
              initialMarkdown={presentation.completePageMarkdown || ""}
              onChange={(e) => {
                onPresentationUpdate({
                  ...presentation,
                  completePageMarkdown: e,
                });
              }}
              isShowEditor={canEditPresentation}
              isServiceProvider={auth.isServiceProviderAccount()}
            />
          ),
        })}
    </div>
  );
};

export const PresentationSettingContainer = connectService<
  { procedure?: ProcedureEntity },
  ProcedurePresentationContextProps,
  ProcedurePresentationService
>(
  PresentationSetting,
  (p) =>
    new ProcedurePresentationService({
      procedure: p.procedure!,
    }),
);
